import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useStore } from '../../../store'
import { useThemeStore } from '../../../store/ConfigStore';
import { useToast } from '@/components/Toast';
import { validateNumericID } from '@/utils/validator';
import type { Dayjs } from 'dayjs';
import { Table, Drawer, Input, DatePicker, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import { Pagination } from "@/components/Pagination";
import type { DrawerMode, ClientList, ClientDetails, ClientHistoryCommission } from '@/types/customerManagement';
import { addClient, fetchClientDetails, fetchClientList, fetchHistory, getRules, modifyClient, exportClientList, modifyClientRemark } from '@/services/customerManagement';
import { MobileTableCard } from '@/components/MobileCards';
import dayjs from 'dayjs';
import avatarIcon from '@/assets/dataChart/avatar-icon.svg'
import CopyIcon from '../../../assets/svg/copy.svg';
import CopyDarkIcon from '../../../assets/svg/copyDark.svg';
import CheckIcon from '../../../assets/svg/check-line.svg';
import CheckDarkIcon from '../../../assets/svg/check-line-dark.svg';

export const routeConfig: RouteConfig = {
    title: 'route.customerManagement.clientList',
    layout: true,
    sort: 0
};

const ClientList = () => {
    const { t } = useLocale();
    const { UserStore } = useStore();
    const isDark = useThemeStore(); // 引入主题状态
    const toast = useToast();

    // const [selectedUID, setSelectedUID] = useState(0);
    const [UID, setUID] = useState<string>('');
    const [customerEmail, setCustomerEmail] = useState<string>('');
    const [selectedCustomerType, setSelectedCustomerType] = useState<number | null>(null);
    const [transactionTime, setTransactionTime] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(1689081600);
    const [clientListData, setClientListData] = useState<ClientList[]>([]);
    const [clientDetailsData, setClientDetailsData] = useState<ClientDetails[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState<number>(0);
    const [copiedUID, setCopiedUID] = useState<string | null>(null);
    
    // For managing client drawer
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<DrawerMode>('manage');
    const [selectedRecord, setSelectedRecord] = useState<ClientList | null>(null);
    const [maxSpotRatio, setMaxSpotRatio] = useState<number>(0);
    const [maxFuturesRatio, setMaxFuturesRatio] = useState<number>(0);

    // For viewing client details
    const [viewingRecord, setViewingRecord] = useState<ClientDetails | null>(null);
    const [clientHistoryCommissions, setClientHistoryCommissions] = useState<ClientHistoryCommission[]>([]);

    const assetRangeOptions = [
        { value: null, label: t('customerManagement.clientList.none') },
        { value: 0, label: '0 ~ 1,000' },
        { value: 1, label: '1,000 ~ 5,000' },
        { value: 2, label: '5,000 ~ 20,000' },
        { value: 3, label: '20,000+' },
    ];

    // Helper to build params for client list API
    const buildClientListParams = (UID: string, customerEmail: string, selectedCustomerType: number | null, transactionTime: [Dayjs | null, Dayjs | null]) => {
        let params: any = {};
        if (UID !== '') {
            params.user_id = UID;
        }
        if (customerEmail !== '') {
            params.query = customerEmail; // email/wallet address
        }
        if (selectedCustomerType !== null) {
            params.type = selectedCustomerType; // 0=direct, 1=indirect
        }
        if (transactionTime[0] && transactionTime[1]) {
            params.time_range = [transactionTime[0].unix(), transactionTime[1].unix()];
        }
        return params;
    };

    const handleReset = async () => {
        try {
            // setSelectedUID(0)
            setUID('');
            setSelectedCustomerType(null);
            setTransactionTime([null, null]);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            // fetch new data based on the reset filters
            const res = await fetchClientList({}, 1, pagination.pageSize);
            setClientListData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = async () => {
        try {
            // Validate UID before querying
            if (UID.trim() !== '') {
                const validation = validateNumericID(UID, 'UID');
                if (!validation.isValid) {
                    toast.error({
                        message: t(validation.message)
                    });
                    return;
                }
            }

            const params = buildClientListParams(UID, customerEmail, selectedCustomerType, transactionTime);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            const res = await fetchClientList(params, 1, pagination.pageSize);
            setClientListData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while querying.'
            });
        }
    };

    const handleManageClick = (record: ClientList) => {
        try {
            if (record) {
                // Clone to avoid direct mutation
                setSelectedRecord({ ...record });
                setDrawerMode('manage');
                setDrawerOpen(true);
            }
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.manageError') || 'An error occurred while opening manage drawer.'
            });
        }
    };

    const handleAddAgentClick = async (record: ClientList) => {
        try {
            if (record) {
                const res = await getRules();

                // Clone to avoid direct mutation and set default ratios
                setSelectedRecord({ 
                    ...record, 
                    spotRatio: Number(res.invite_spot_ratio),
                    futuresRatio: Number(res.invite_reward_ratio)
                });
                setDrawerMode('addAgent');
                setDrawerOpen(true);
            }
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.addAgentError') || 'An error occurred while opening add agent drawer.'
            });
        }
    };

    const handleRemarkClick = (record: ClientList) => {
        try {
            if (record) {
                // Clone to avoid direct mutation
                setSelectedRecord({ ...record });
                setDrawerMode('editRemark');
                setDrawerOpen(true);
            }
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.remarkError') || 'An error occurred while opening remark drawer.'
            });
        }
    };

    const handleDrawerClose = () => {
        try {
            setDrawerOpen(false);
            setSelectedRecord(null);
            setDrawerMode('manage');
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.manageError') || 'An error occurred while closing drawer.'
            });
        }
    };

    const handleDrawerSave = async () => {
        try {
            if (selectedRecord) {
                if (drawerMode === 'addAgent') {
                    // Add client as agent
                    await addClient({
                        user_id: selectedRecord.uid,
                        reward_ratio: selectedRecord.futuresRatio,
                        spot_ratio: selectedRecord.spotRatio,
                        rake_back_time: 1,
                        valid: true
                    });
                } else if (drawerMode === 'editRemark') {
                    // Edit remark only
                    await modifyClientRemark({
                        user_id: selectedRecord.uid,
                        remark: selectedRecord.label,
                    });
                } else {
                    // Manage existing client - only allow modifying client ratio for customerType 0 (direct)
                    if (selectedRecord.customerType !== 0) {
                        toast.error({
                            message: t('customerManagement.clientList.modifyDirectOnly') || 'You can only modify the client ratio of direct customers.'
                        });
                        setTimeout(() => {
                            setDrawerOpen(false);
                            setSelectedRecord(null);
                        }, 100); // 100ms delay to allow toast to render
                        return;
                    }
                    
                    // Find the original record for comparison
                    const original = clientListData.find(item => item.uid === selectedRecord.uid);
                    if (original) {
                        // Only allow increasing ratios
                        if (
                            (selectedRecord.spotRatio ?? 0) < (original.spotRatio ?? 0) ||
                            (selectedRecord.futuresRatio ?? 0) < (original.futuresRatio ?? 0)
                        ) {
                            toast.error({
                                message: t('customerManagement.clientList.modifyRatioIncreaseOnly') || 'You can only increase the ratios.'
                            });
                            return;
                        }
                    }

                    await modifyClient({
                        user_id: selectedRecord.uid,
                        reward_ratio: selectedRecord.futuresRatio,
                        spot_ratio: selectedRecord.spotRatio,
                        rake_back_time: 1,
                        remark: selectedRecord.label,
                        valid: true
                    });
                }

                // Refresh the client list
                const params = buildClientListParams(UID, customerEmail, selectedCustomerType, transactionTime);
                try {
                    const res = await fetchClientList(params, 1, pagination.pageSize);
                    setClientListData(res.list);
                    setTotal(res.total);
                } catch (fetchError: any) {
                    toast.error({
                        message: fetchError?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while refreshing client list.'
                    });
                }

                toast.success({
                    message: t('customerManagement.clientList.modifySuccess') || 'Saved successfully.'
                }); 
            }
        } catch (error: any) {
            toast.error({
                message: error?.message || (drawerMode === 'addAgent' 
                    ? t('customerManagement.clientList.addAgentError') 
                    : drawerMode === 'editRemark'
                    ? t('customerManagement.clientList.remarkError') 
                    : t('customerManagement.clientList.modifyError')) || 'An error occurred while saving.'
            });
        } finally {
            setDrawerOpen(false);
            setSelectedRecord(null);
            setDrawerMode('manage');
        }
    };

    const handleViewDetails = async (record: ClientList) => {
        try {
            const details = await fetchClientDetails({ user_id: record.uid });
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            if (details) {
                // Clone to avoid direct mutation
                setViewingRecord({ ...details });
            }
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.detailsError') || 'An error occurred while fetching client details.'
            });
        }
    };

    const handleCloseDetails = () => {
        setViewingRecord(null);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    function exportToCsv(filename: string, rows: any[]) {
        if (!rows.length) return;
        const keys = Object.keys(rows[0]);
        const csvContent =
            keys.join(',') + '\n' +
            rows.map(row => keys.map(k => `"${row[k] ?? ''}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const handleExport = async () => {
        try {
            console.log('Exporting data...');
            setLoading(true);
            const params = buildClientListParams(UID, customerEmail, selectedCustomerType, transactionTime);
            const result = await exportClientList(params);
            
            // Export the data list to CSV
            if (result && result.list && result.list.length > 0) {
                exportToCsv('client_list.csv', result.list);
                toast.success({
                    message: t('financialDetails.commission.exportSuccess') || 'Data exported successfully!'
                });
            } else {
                toast.warning({
                    message: t('financialDetails.commission.noDataToExport') || 'No data to export.'
                });
            }
        } catch (error: any) {
            console.error('Export failed:', error);
            toast.error({
                message: error?.message || t('financialDetails.commission.exportError') || 'Export failed. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    }

    const handleTimeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        setTransactionTime(dates ?? [null, null]);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('customerManagement.clientList.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const formatNumber = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    };

    const handleCopyUID = async (uid: string) => {
        try {
            await navigator.clipboard.writeText(uid);
            setCopiedUID(uid);
            setTimeout(() => setCopiedUID(null), 2000);
            toast.success({
                message: t('customerManagement.clientList.copySuccess') || 'UID copied!'
            });
        } catch (err: any) {
            toast.error({
                message: err?.message || t('customerManagement.clientList.copyError') || 'Failed to copy UID.'
            });
        }
    };

    const queryFields: QueryField[] = [
        // {
        //     type: 'preTabInput',
        //     name: 'selectedUID',
        //     label: "UID",
        //     prefixOptions: [
        //         { value: null, label: t('customerManagement.clientList.customerUID') },
        //         { value: 0, label: t('customerManagement.clientList.superiorAgent') },
        //     ],
        //     prefixValue: selectedUID,
        //     onPrefixChange: (value) => setSelectedUID(value),
        //     value: UID,
        //     onChange: setUID,
        //     placeholder: t('customerManagement.clientList.pleaseEnterPlaceholder'),
        //     colSpan: { mobile: 2, desktop: 2 }
        // },
        {
            type: 'input',
            name: 'UID',
            label: t('customerManagement.clientList.customerUID'),
            value: UID,
            onChange: setUID,
            placeholder: t('customerManagement.clientList.pleaseEnterPlaceholder'),
        },
        {
            type: 'input',
            name: 'customerEmail',
            label: t('customerManagement.clientList.customerEmail'),
            value: customerEmail,
            onChange: setCustomerEmail,
            placeholder: t('customerManagement.clientList.pleaseEnterPlaceholder'),
        },
        {
            type: 'select',
            name: 'customerType',
            label: t('customerManagement.clientList.customerType'),
            value: selectedCustomerType,
            onChange: setSelectedCustomerType,
            options: [
                { value: null, label: t('customerManagement.clientList.all') },
                { value: 0, label: t('customerManagement.clientList.direct') },
                { value: 1, label: t('customerManagement.clientList.indirect') }
            ]
        },
        {
            type: 'dateRange',
            name: 'transactionTime',
            label: t('customerManagement.clientList.transactionTime'),
            value: transactionTime,
            onChange: (dates: [Dayjs, Dayjs] | null) => {
                setTransactionTime(dates ?? [dayjs(0), dayjs(0)]);
            },
            placeholder: [t('customerManagement.clientList.startTime'), t('customerManagement.clientList.endTime')],
        }
    ];

    const clientListColumns: ColumnsType<ClientList> = [
        {
            title: t('customerManagement.clientList.table.uid'),
            dataIndex: 'uid',
            key: 'uid',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text: string) => (
                <div className="flex items-center gap-1">
                    <span>{text}</span>
                    <button
                        className="border-none bg-transparent p-0 m-0"
                        onClick={() => handleCopyUID(text)}
                    >
                        <img
                            src={copiedUID === text ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)}
                            alt={copiedUID === text ? 'copied' : 'copy'}
                            className="w-4 h-4"
                        />
                    </button>
                </div>
            ),
        },
        {
            title: t('customerManagement.clientList.table.label'),
            dataIndex: 'label',
            key: 'label',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text: string) => text || '--'
        },
        {
            title: (
                <div className="flex items-center gap-1">
                    <span>{t('customerManagement.clientList.table.registrationTime')}</span>
                    <Tooltip title={t('customerManagement.clientList.table.registrationTimeTooltip')}>
                        <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-special-text bg-special-text-20 rounded-full cursor-help">?</span>
                    </Tooltip>
                </div>
            ),
            dataIndex: 'registrationTime',
            key: 'registrationTime',
            align: 'start',
            width: 160,
            ellipsis: true,
            render: (text, record) => {
                // Handle undefined/null lastTransactionTime
                if (!record.lastTransactionTime || record.lastTransactionTime === '0') {
                    return (
                        <span className="flex flex-col text-xs gap-1">
                            {formatDate(text, true)}
                            {text && (
                                <span className="inline-flex items-center bg-leverage-20 text-normal rounded-md px-2 py-0.5 w-auto" style={{ width: 'fit-content', minWidth: 0 }}>
                                    {t('customerManagement.clientList.table.inactive')}
                                </span>
                            )}
                        </span>
                    );
                }

                const lastTransactionTimestamp = typeof record.lastTransactionTime === 'string' && /^\d{10}$/.test(record.lastTransactionTime)
                    ? Number(record.lastTransactionTime) * 1000
                    : new Date(record.lastTransactionTime).getTime();
                const now = Date.now();
                const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
                const isActive = now - lastTransactionTimestamp < sevenDaysMs;

                return (
                    <span className="flex flex-col text-xs gap-1">
                        {formatDate(text, true)}
                        {text && (
                            isActive ? 
                                <span className="inline-flex items-center bg-special-text-20 text-special-text rounded-md px-2 py-0.5 w-auto" style={{ width: 'fit-content', minWidth: 0 }}>
                                    {t('customerManagement.clientList.table.active')}
                                </span> : 
                                <span className="inline-flex items-center bg-leverage-20 text-normal rounded-md px-2 py-0.5 w-auto" style={{ width: 'fit-content', minWidth: 0 }}>
                                    {t('customerManagement.clientList.table.inactive')}
                                </span>
                        )}
                    </span>
                );
            }
        },
        {
            title: t('customerManagement.clientList.table.customerType'),
            dataIndex: 'customerType',
            key: 'customerType',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text) => (
                text === 0 ? t('customerManagement.clientList.direct') : text === 1 ? t('customerManagement.clientList.indirect') : t('customerManagement.superAgent.none')
            )
        },
        {
            title: t('customerManagement.clientList.table.superiorAgentUID'),
            dataIndex: 'superiorAgentUID',
            key: 'superiorAgentUID',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => (
                text === '0' || text === 0 ? t('customerManagement.superAgent.none') : text
            )
        },
        {
            title: t('customerManagement.clientList.table.username'),
            dataIndex: 'username',
            key: 'username',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal">{text}</span>
        },
        {
            title: t('customerManagement.clientList.table.tradingVolume'),
            dataIndex: 'tradingVolume',
            key: 'tradingVolume',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (value: number) => {
                return <span className="text-xs">${formatNumber(value)}</span>;
            },
        },
        {
            title: t('customerManagement.clientList.table.handlingFee'),
            dataIndex: 'handlingFee',
            key: 'handlingFee',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (value: number) => {
                return <span className="text-xs">${formatNumber(value)}</span>;
            },
        },
        {
            title: t('customerManagement.clientList.table.commissions'),
            dataIndex: 'commissions',
            key: 'commissions',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (value: number) => {
                return <span className="text-xs">${formatNumber(value)}</span>;
            },
        },
        {
            title: t('customerManagement.clientList.table.deposit'),
            dataIndex: 'deposit',
            key: 'deposit',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (value: number) => {
                return <span className="text-xs">${formatNumber(value)}</span>;
            },
        },
        // {
        //     title: t('customerManagement.clientList.table.associatedGroup'),
        //     dataIndex: 'associatedGroup',
        //     key: 'associatedGroup',
        //     align: 'start',
        //     width: 130,
        //     ellipsis: true
        // },
        {
            title: t('customerManagement.clientList.table.assetRange'),
            dataIndex: 'assetRange',
            key: 'assetRange',
            align: 'start',
            width: 200,
            ellipsis: true,
            render: (value: number) => <span className="text-xs">{assetRangeOptions.find(option => option.value === value)?.label}</span>
        },
        {
            title: t('customerManagement.clientList.table.firstDepositTime'),
            dataIndex: 'firstDepositTime',
            key: 'firstDepositTime',
            align: 'start',
            width: 160,
            ellipsis: true,
            render: (text) => <span className="text-xs">{formatDate(text, false)}</span>
        },
        {
            title: t('customerManagement.clientList.table.firstTransactionTime'),
            dataIndex: 'firstTransactionTime',
            key: 'firstTransactionTime',
            align: 'start',
            width: 160,
            ellipsis: true,
            render: (text) => <span className="text-xs">{formatDate(text, false)}</span>
        },
        {
            title: t('customerManagement.clientList.table.lastTransactionTime'),
            dataIndex: 'lastTransactionTime',
            key: 'lastTransactionTime',
            align: 'start',
            width: 160,
            ellipsis: true,
            render: (text) => <span className="text-xs">{formatDate(text, false)}</span>
        },
        {
            title: t('customerManagement.clientList.table.operation'),
            dataIndex: 'operation',
            key: 'operation',
            align: 'end',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <div className="grid grid-cols-3 md:flex items-center justify-start md:justify-end gap-2">
                    { !record.isAgent && record.customerType === 0 && (
                        <button 
                            className="text-sm font-normal text-special-text font-inter bg-transparent border-none flex justify-center items-center"
                            onClick={() => handleAddAgentClick(record)}
                        >
                            {t('customerManagement.clientList.table.addAgent')}
                        </button>
                    )}
                    <button
                        className="text-sm font-normal text-special-text font-inter bg-transparent border-none flex justify-center items-center"
                        onClick={() => handleViewDetails(record)}
                    >
                        {t('customerManagement.clientList.table.view')}
                    </button>
                    <button 
                        className="text-sm font-normal text-special-text disabled:opacity-50 font-inter bg-transparent border-none flex justify-center items-center"
                        onClick={() => handleManageClick(record)}
                        disabled={!record.isAgent || record.customerType !== 0} // Only direct agent customers can be managed
                    >
                        {t('customerManagement.clientList.table.manage')}
                    </button>
                    <button 
                        className="text-sm font-normal text-special-text disabled:opacity-50 font-inter bg-transparent border-none flex justify-center items-center"
                        onClick={() => handleRemarkClick(record)}
                    >
                        {t('customerManagement.clientList.table.remark')}
                    </button>
                </div>
            )
        }
    ];

    const clientHistoryCommissionsColumns: ColumnsType<ClientHistoryCommission> = [
        {
            title: t('customerManagement.clientList.table.transactionUID'),
            dataIndex: 'transactionUID',
            key: 'transactionUID',
            align: 'start',
            width: 160,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal">{text}</span>
        },
        {
            title: t('customerManagement.clientList.table.transactionMethod'),
            dataIndex: 'transactionMethod',
            key: 'transactionMethod',
            align: 'start',
            width: 120,
            // transactionMethod: number[]; // entrust_type / order_type 
            // order_type 0: 市价单 1：计划单 2：止盈单 4：止损单 5：强平单 0: Market Order 1: Limit Order 2: Take Profit Order 4: Stop Loss Order 5: Forced Liquidation Order
            // entrust_type 0-市价 1-限价 2-强平单 0-Market Price 1-Limit Price 2-Liquidation Order
            render: (value: number[]) => {
                // Lookup tables
                const entrustTypeMap: Record<number, string> = {
                    0: t('customerManagement.clientList.entrustType.market'),   // 市价
                    1: t('customerManagement.clientList.entrustType.limit'),    // 限价
                    2: t('customerManagement.clientList.entrustType.liquidation'), // 强平单
                };
                // const orderTypeMap: Record<number, string> = {
                //     0: t('customerManagement.clientList.orderType.marketOrder'),      // 市价单
                //     1: t('customerManagement.clientList.orderType.planOrder'),        // 计划单
                //     2: t('customerManagement.clientList.orderType.takeProfitOrder'),  // 止盈单
                //     4: t('customerManagement.clientList.orderType.stopLossOrder'),    // 止损单
                //     5: t('customerManagement.clientList.orderType.forcedLiquidationOrder'), // 强平单
                // };

                const [entrustType, /*orderType*/] = value || [];
                return (
                    <div className="flex items-center">
                        <span>
                            {entrustTypeMap[entrustType] || entrustType}
                            {/* {' / '} */}
                            {/* {orderTypeMap[orderType] || orderType} */}
                        </span>
                    </div>
                );
            },
        },
        {
            title: t('customerManagement.clientList.table.tradingPair'),
            dataIndex: 'tradingPair',
            key: 'tradingPair',
            align: 'start',
            width: 120,
        },
        {
            title: t('customerManagement.clientList.table.direction'),
            dataIndex: 'direction',
            key: 'direction',
            align: 'start',
            width: 120,
            render: (text) => {
                const directionMap: Record<string, string> = {
                    B: t('customerManagement.clientList.direction.buy'),
                    S: t('customerManagement.clientList.direction.sell'),
                };
                return directionMap[text] || text;
            },
        },
        {
            title: t('customerManagement.clientList.table.subjectiveMethod'),
            dataIndex: 'subjectiveMethod',
            key: 'subjectiveMethod',
            align: 'start',
            width: 120,
            render: (text) => {
                const methodMap: Record<number, string> = {
                    0: t('customerManagement.clientList.role.maker'),
                    1: t('customerManagement.clientList.role.taker'),
                };
                return methodMap[text] || text;
            }
        },
        {
            title: t('customerManagement.clientList.table.averageTransactionPrice'),
            dataIndex: 'averageTransactionPrice',
            key: 'averageTransactionPrice',
            align: 'start',
            width: 120,
            render: (value: number) => <span className="break-all whitespace-normal">{formatNumber(value)}</span>
        },
        {
            title: t('customerManagement.clientList.table.executedOrderedVolume'),
            dataIndex: 'executedOrderedVolume',
            key: 'executedOrderedVolume',
            align: 'start',
            width: 120,
            render: (value: number[]) => {
                const [quote_volume, usdt_volume] = value || [];
                return (
                    <div className="flex items-center break-all whitespace-normal">
                        <span>
                            {formatNumber(quote_volume)}
                            {' / '}
                            {formatNumber(usdt_volume)}
                        </span>
                    </div>
                );
            },
        },
        {
            title: t('customerManagement.clientList.table.transactionFee'),
            dataIndex: 'transactionFee',
            key: 'transactionFee',
            align: 'start',
            width: 120,
            render: (value: number) => <span className="break-all whitespace-normal">{formatNumber(value)}</span>
        },
        {
            title: t('customerManagement.clientList.table.orderStatus'),
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            align: 'start',
            width: 120,
            render: (text) => {
                const statusMap: Record<number, string> = {
                    0: t('customerManagement.clientList.status.default'),
                    100: t('customerManagement.clientList.status.userRevocation'),
                    101: t('customerManagement.clientList.status.marketUnfilledRevocation'),
                    102: t('customerManagement.clientList.status.backgroundRevocation'),
                    200: t('customerManagement.clientList.status.full'),
                    201: t('customerManagement.clientList.status.partialRetracted'),
                    202: t('customerManagement.clientList.status.partialSlot'),
                };
                return statusMap[text] || text;
            },
        },
        {
            title: t('customerManagement.clientList.table.creationTime'),
            dataIndex: 'creationTime',
            key: 'creationTime',
            align: 'start',
            width: 150,
            render: (value: string) => formatDate(value)
        }
    ];

    const mobileClientListColumns = clientListColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    const mobileClientHistoryCommissionsColumns = clientHistoryCommissionsColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                setMaxSpotRatio(UserStore.userSpotRatio); // Example max spot ratio
                setMaxFuturesRatio(UserStore.userRewardRatio); // Example max futures ratio

                try {
                    const params = buildClientListParams(UID, customerEmail, selectedCustomerType, transactionTime);
                    const clientRes = await fetchClientList(params, pagination.current, pagination.pageSize);
                    setClientListData(clientRes.list);
                    setTotal(clientRes.total);
                } catch (clientError: any) {
                    toast.error({
                        message: clientError?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching client list.'
                    });
                }
                setLastUpdated(Math.floor(Date.now() / 1000));
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        if (!viewingRecord) {
            fetchData();
        } else {
            // Fetch client history commissions when viewing details
            const fetchHistoryData = async () => {
                setLoading(true);
                try {
                    const params: any = { user_id: viewingRecord.uid };
                    if (transactionTime[0] && transactionTime[1]) {
                        params.time_range = [transactionTime[0].unix(), transactionTime[1].unix()];
                    }
                    const res = await fetchHistory(params, pagination.current, pagination.pageSize);
                    setClientHistoryCommissions(res.list);
                    setTotal(res.total);
                } catch (error: any) {
                    toast.error({
                        message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching data.'
                    });
                } finally {
                    setLoading(false);
                }
            };
            fetchHistoryData();
        }
    }, [pagination.current, pagination.pageSize, viewingRecord, transactionTime]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            { viewingRecord ? (
                <>
                    <div className="flex gap-[10px] items-center pb-6">
                        <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                            {t('customerManagement.clientList.title')}
                        </span>
                        <span className="text-xs font-normal font-inter leading-normal text-normal dark:text-dark-normal">
                            {t('customerManagement.clientList.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                        </span>
                    </div>
                    <div className="flex items-center gap-[10px] text-sm font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal mb-6">
                        <span
                            className="cursor-pointer text-normal dark:text-dark-normal hover:text-sel-normal hover:dark:text-dark-sel-normal flex items-center text-sm font-normal font-inter leading-normal"
                            onClick={handleCloseDetails}
                        >
                            {t('customerManagement.clientList.title')}
                        </span>
                        <svg className="w-4 h-4 text-normal dark:text-dark-normal" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        <span className="text-sm font-normal font-inter leading-normal">{t('customerManagement.clientList.customerDetails')}</span>
                    </div>
                    <div className="flex flex-col gap-2 p-6 mb-6 bg-card-background dark:bg-dark-card-background rounded-lg text-sm font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                        <div className="flex mb-6">{t('customerManagement.clientList.details.title')}</div>
                        <div className="p-6 border border-normal dark:border-dark-normal rounded-lg">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 justify-between">
                                {/* Avatar horizontally aligned */}
                                <div className="flex flex-col sm:flex-row items-center">
                                    <img src={avatarIcon} alt="Avatar Icon" className="w-16 h-16 mr-2" />
                                    <div className="flex flex-col justify-center gap-y-1">
                                        {/* Email */}
                                        <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                                            {viewingRecord?.email}
                                        </span>
                                        {/* UID */}
                                        <span className="text-center sm:text-left text-sm font-normal font-inter leading-normal text-normal dark:text-dark-normal">
                                            UID: {viewingRecord?.uid}
                                        </span>
                                    </div>
                                </div>
                                {/* Ratios */}
                                <div className="grid grid-cols-2 gap-4 items-start text-center">
                                    <div className="flex flex-col">
                                        <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                                            {typeof viewingRecord.spotRatio === 'number'
                                                ? (viewingRecord.spotRatio % 1 !== 0 && viewingRecord.spotRatio.toString().split('.')[1]?.length > 2
                                                    ? viewingRecord.spotRatio.toFixed(2)
                                                    : viewingRecord.spotRatio)
                                                : 0}%
                                        </span>
                                        <span className="text-xs font-normal font-inter leading-normal text-normal dark:text-dark-normal">{t('customerManagement.clientList.details.spot')}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                                            {typeof viewingRecord.futuresRatio === 'number'
                                                ? (viewingRecord.futuresRatio % 1 !== 0 && viewingRecord.futuresRatio.toString().split('.')[1]?.length > 2
                                                    ? viewingRecord.futuresRatio.toFixed(2)
                                                    : viewingRecord.futuresRatio)
                                                : 0}%
                                        </span>
                                        <span className="text-xs font-normal font-inter leading-normal text-normal dark:text-dark-normal">{t('customerManagement.clientList.details.futures')}</span>
                                    </div>
                                    {/* <div className="flex flex-col">
                                        <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-sel-dark-normal">
                                            {typeof viewingRecord.alphaRatio === 'number'
                                                ? (viewingRecord.alphaRatio % 1 !== 0 && viewingRecord.alphaRatio.toString().split('.')[1]?.length > 2
                                                    ? viewingRecord.alphaRatio.toFixed(2)
                                                    : viewingRecord.alphaRatio)
                                                : 0}%
                                        </span>
                                        <span className="text-xs font-normal font-inter leading-normal text-normal dark:text-dark-normal">{t('customerManagement.clientList.details.alpha')}</span>
                                    </div> */}
                                </div>
                            </div>
                            {/* Horizontal Divider */}
                            <div className="mt-6 mb-10 border-t border-normal dark:border-dark-normal" />
                            {/* Additional details can be added here */}
                            <div className="grid grid-cols-1 md:grid-cols-4 items-center md:items-start gap-x-8 gap-y-4">
                                <div className="flex flex-col items-center md:items-start gap-[9px]">
                                    <div className="text-xs font-normal font-inter leading-3 text-normal dark:text-dark-normal">
                                        {t('customerManagement.clientList.details.customerType')}
                                    </div>
                                    <div className="text-sx font-normal font-inter leading-3 text-sel-normal dark:text-dark-sel-normal">
                                        {viewingRecord.customerType === 0 ? t('customerManagement.clientList.details.directClient') : t('customerManagement.clientList.details.indirectClient')}
                                    </div>
                                </div>
                                <div className="flex flex-col items-center md:items-start gap-[9px]">
                                    <div className="text-xs font-normal font-inter leading-3 text-normal dark:text-dark-normal">
                                        {t('customerManagement.clientList.details.superiorAgentUID')}
                                    </div>
                                    <div className="text-sx font-normal font-inter leading-3 text-sel-normal dark:text-dark-sel-normal">
                                        {viewingRecord.superiorAgentUID === "0" ? UserStore.userId  : viewingRecord.superiorAgentUID || t('customerManagement.clientList.details.none')}
                                    </div>
                                </div>
                                {/* <div className="flex flex-col items-center md:items-start gap-[9px]">
                                    <div className="text-xs font-normal font-inter leading-3 text-normal dark:text-dark-normal">
                                        {t('customerManagement.clientList.details.group')}
                                    </div>
                                    <div className="text-sx font-normal font-inter leading-3 text-sel-normal dark:text-dark-sel-normal">
                                        {viewingRecord.group === '-' ? t('customerManagement.clientList.details.none') : viewingRecord.group}
                                    </div>
                                </div> */}
                                <div className="flex flex-col items-center md:items-start gap-[9px]">
                                    <div className="text-xs font-normal font-inter leading-3 text-normal dark:text-dark-normal">
                                        {t('customerManagement.clientList.details.registrationTime')}
                                    </div>
                                    <div className="text-sx font-normal font-inter leading-3 text-sel-normal dark:text-dark-sel-normal">
                                        {formatDate(viewingRecord.registrationTime)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card-background dark:bg-card-background rounded-lg p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div className="text-sel-normal dark:text-dark-sel-normal text-xl font-normal font-inter leading-normal">
                                {t('customerManagement.clientList.table.history')}
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center w-[280px]">
                                    <DatePicker.RangePicker
                                        className="query-selector w-full bg-card-background dark:bg-dark-card-background border border-normal dark:border-dark-normal rounded-md"
                                        value={transactionTime}
                                        onChange={handleTimeChange}
                                        placeholder={[t('customerManagement.clientList.startTime'), t('customerManagement.clientList.endTime')]}
                                        format={'YYYY-MM-DD'}
                                        allowClear={false}
                                        getPopupContainer={() => document.body}
                                        style={{
                                            height: '2rem',
                                            fontSize: '0.75rem',
                                            fontFamily: 'Inter, sans-serif',
                                        }}
                                    />
                                </div>
                                <button
                                    onClick={() => handleTimeChange(null)}
                                    className="h-8 bg-card-background dark:bg-dark-card-background border border-dark-main dark:border-main text-sel-normal dark:text-dark-sel-normal opacity-60 hover:opacity-50 text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded whitespace-nowrap"
                                >
                                    {t('customerManagement.clientList.reset')}
                                </button>
                            </div>
                        </div>
                        <div className="hidden md:block h-[500px] overflow-hidden overflow-y-auto overflow-x-auto">
                            <Table
                                dataSource={clientHistoryCommissions}
                                columns={clientHistoryCommissionsColumns}
                                rowKey="transactionUID"
                                loading={loading}
                                size="small"
                                // scroll={{ x: 1200 }}
                                className="custom-table"
                                showHeader={true}
                                pagination={false}
                                locale={{ emptyText: '' }}
                                components={{
                                    body: {
                                        wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                                    }
                                }}
                            />
                        </div>

                        <div className="block md:hidden">
                            {clientHistoryCommissions.map((row, idx) => (
                                <MobileTableCard
                                    key={row.transactionUID}
                                    record={row}
                                    columns={mobileClientHistoryCommissionsColumns}
                                    t={t}
                                />
                            ))}
                        </div>

                        <div className="mt-4 flex justify-end">
                            <Pagination
                                currentPage={pagination.current}
                                totalPages={Math.ceil(total / pagination.pageSize)}
                                onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                                pageSize={pagination.pageSize}
                                setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                                pageSizes={[10, 20, 50, 100]}
                                isPageSizeVisible={true}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <GenericQueryCard
                        title={t('customerManagement.clientList.title')}
                        subtitle={t('customerManagement.clientList.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                        fields={queryFields}
                        onExport={handleExport}
                        onReset={handleReset}
                        onQuery={handleQuery}
                        gridCols="grid-cols-2 md:grid-cols-4"
                    />
                    
                    {/* Table Container with Custom Layout */}
                    <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                        {/* Table Content */}
                        <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                            <Table
                                dataSource={clientListData}
                                columns={clientListColumns}
                                rowKey="uid"
                                pagination={false}
                                loading={loading}
                                size="small"
                                // scroll={{ x: 1200 }}
                                className="custom-action-table"
                                showHeader={true}
                                locale={{ emptyText: '' }}
                                components={{
                                    body: {
                                        wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                                    }
                                }}
                            />
                        </div>

                        <div className="block md:hidden">
                            {clientListData.map((row, idx) => (
                                <MobileTableCard
                                    key={row.uid}
                                    record={row}
                                    columns={mobileClientListColumns}
                                    t={t}
                                />
                            ))}
                        </div>

                        <div className="mt-4 flex justify-end">
                            <Pagination
                                currentPage={pagination.current}
                                totalPages={Math.ceil(total / pagination.pageSize)}
                                onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                                pageSize={pagination.pageSize}
                                setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                                pageSizes={[10, 20, 50, 100]}
                                isPageSizeVisible={true}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <Drawer
                        title={
                            <div className="text-sel-normal dark:text-dark-sel-normal text-xl font-normal font-inter flex justify-between">
                                <div>
                                    {drawerMode === 'addAgent' 
                                        ? t('customerManagement.clientList.addAgent.title') || 'Add as Agent'
                                        : drawerMode === 'editRemark'
                                        ? t('customerManagement.clientList.remark.title') || 'Edit Remark'
                                        : t('customerManagement.clientList.manage.title')
                                    }
                                </div>
                                <svg 
                                    onClick={handleDrawerClose} 
                                    className="cursor-pointer text-chart-title-text hover:text-white transition-colors" 
                                    width="24" 
                                    height="24" 
                                    viewBox="0 0 24 24" 
                                    fill="none"
                                >
                                    <path 
                                        d="M18 6L6 18M6 6l12 12" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                    />
                                </svg>
                            </div>
                        }
                        placement="right"
                        size="large"
                        closeIcon={false}
                        onClose={handleDrawerClose}
                        open={drawerOpen}
                        zIndex={30}
                        styles={{
                            header: {
                                background: '#16182E',
                                borderBottom: '0px',
                                padding: '20px 24px'
                            },
                            body: {
                                background: '#16182E',
                                padding: '24px'
                            }
                        }}
                    >
                        {selectedRecord && (
                            <div className="space-y-6">
                                {/* Client UID Section */}
                                <div className="space-y-[6px]">
                                    <div className="flex items-start gap-[6px]">
                                        <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientList.manage.clientUID')}</span>
                                    </div>
                                    <div className="bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px]">
                                        <span className="text-normal dark:text-dark-normal text-xs font-medium font-inter leading-none">{selectedRecord.uid}</span>
                                    </div>
                                </div>

                                {/* Client Label/Remarks Section - Show for editRemark mode */}
                                {(drawerMode === 'editRemark') && (
                                    <div className="space-y-[6px]">
                                        <div className="flex items-start gap-[6px]">
                                            <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientList.remark.label')}</span>
                                        </div>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder={t('customerManagement.clientList.remark.placeholder')}
                                                className="bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter"
                                                maxLength={30}
                                                onChange={e => setSelectedRecord(r => r ? { ...r, label: e.target.value } : r)}
                                                value={selectedRecord?.label}
                                            />
                                            <span className="absolute right-3 top-[15px] text-chart-title-text text-sm font-medium font-sans leading-tight">{(selectedRecord?.label || '').length}/30</span>
                                        </div>
                                    </div>
                                )}

                                {/* Commission Settings Section - Hide for editRemark mode */}
                                {drawerMode !== 'editRemark' && (
                                <div className="flex-col">
                                    <div className="flex items-start gap-[6px] mb-[6px]">
                                        <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">
                                            {drawerMode === 'addAgent' 
                                                ? t('customerManagement.clientList.addAgent.commissionSettings') || 'Set Commission Ratios'
                                                : t('customerManagement.clientList.manage.commissionSettings')
                                            }
                                        </span>
                                    </div>

                                    {/* Spot Trading */}
                                    <div className="space-y-3 mb-4 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientList.manage.spot')}</span>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                placeholder={t('customerManagement.clientList.manage.clientRatio')}
                                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                                className="flex flex-1 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                                onChange={e => {
                                                    let value = e.target.value.replace(/\D/g, '');
                                                    let num = Number(value);
                                                    if (num > maxSpotRatio) num = maxSpotRatio;
                                                    setSelectedRecord(r => r ? { ...r, spotRatio: num, spotMyRatio: (maxSpotRatio - num) } : r);
                                                }}
                                                value={selectedRecord?.spotRatio ?? 0}
                                            />
                                            <div>+</div>
                                            <Input
                                                placeholder={t('customerManagement.clientList.manage.clientRatio')}
                                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                                className="flex flex-1 bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                                value={(maxSpotRatio - (selectedRecord?.spotRatio ?? 0))}
                                                disabled={true}
                                            />
                                            <div>=</div>
                                            <span className="text-special-text text-sm font-medium font-sans leading-tight">{maxSpotRatio}%</span>
                                        </div>
                                    </div>

                                    {/* Futures Trading */}
                                    <div className="space-y-3 mb-4 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientList.manage.futures')}</span>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                placeholder={t('customerManagement.clientList.manage.clientRatio')}
                                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                                className="flex flex-1 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                                onChange={e => {
                                                    let value = e.target.value.replace(/\D/g, '');
                                                    let num = Number(value);
                                                    if (num > maxFuturesRatio) num = maxFuturesRatio;
                                                    setSelectedRecord(r => r ? { ...r, futuresRatio: num, futuresMyRatio: (maxFuturesRatio - num) } : r);
                                                }}
                                                value={selectedRecord?.futuresRatio ?? 0}
                                            />
                                            <div>+</div>
                                            <Input
                                                placeholder={t('customerManagement.clientList.manage.myRatio')}
                                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                                className="flex flex-1 bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                                value={(maxFuturesRatio - (selectedRecord?.futuresRatio ?? 0))}
                                                disabled={true}
                                            />
                                            <div>=</div>
                                            <span className="text-special-text text-sm font-medium font-sans leading-tight">{maxFuturesRatio}%</span>
                                        </div>
                                    </div>
                                </div>
                                )}

                                {/* {drawerMode === 'manage' && (
                                    <div className="flex items-start gap-[6px]">
                                        <span className="text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none">*</span>
                                        <span className="text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientList.manage.modifyNote')}</span>
                                    </div>
                                )}

                                {drawerMode === 'addAgent' && (
                                    <div className="flex items-start gap-[6px]">
                                        <span className="text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none">*</span>
                                        <span className="text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none">
                                            {t('customerManagement.clientList.addAgent.note') || 'Setting commission ratios will make this customer your agent.'}
                                        </span>
                                    </div>
                                )}

                                {drawerMode === 'editRemark' && (
                                    <div className="flex items-start gap-[6px]">
                                        <span className="text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none">*</span>
                                        <span className="text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none">
                                            {t('customerManagement.clientList.remark.note') || 'You can add or edit a remark for this customer.'}
                                        </span>
                                    </div>
                                )} */}

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-2 pt-6 pb-6 px-6">
                                    <button
                                        onClick={handleDrawerClose}
                                        className="bg-transparent border border-dark-main dark:border-main opacity-60 rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                                    >
                                        {t('customerManagement.clientList.manage.cancel')}
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleDrawerSave();
                                        }}
                                        className="bg-tabline border-none rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                                    >
                                        {drawerMode === 'addAgent' 
                                            ? t('customerManagement.clientList.addAgent.confirm') || 'Add as Agent'
                                            : drawerMode === 'editRemark'
                                            ? t('customerManagement.clientList.remark.confirm') || 'Save Remark'
                                            : t('customerManagement.clientList.manage.confirm')
                                        }
                                    </button>
                                </div>
                            </div>
                        )}
                    </Drawer>
                </>
            )}
        </div>
    );
};

export default ClientList;
